<template>
    <div class="share-url">
        <div class="copy-link-input">
            <input-group v-tooltip="'Copy link'" @click="copyLink">
                <input-text :value="urlOfPage" readonly />
                <input-group-addon>
                    <font-awesome-icon :icon="['fas', 'copy']" class="link" />
                </input-group-addon>
            </input-group>
        </div>
        <div class="button-holder">
            <a :href="linkedInUrl" @click.prevent="shareLinkedin">
                <primevue-button class="plain-btn">
                    LinkedIn
                    <font-awesome-icon :icon="['fab', 'linkedin']" />
                </primevue-button>
            </a>
            <primevue-button @click="shareEmail" class="plain-btn">
                Email
                <font-awesome-icon :icon="['fas', 'envelope']" />
            </primevue-button>
            <primevue-button @click="shareTeams" class="plain-btn">
                Teams
                <img :src="'/images/logos/Teams.svg'" alt="" class="teams-image" />
            </primevue-button>
        </div>
    </div>
</template>

<script setup lang="ts">
import PrimevueButton from 'primevue/button';
import InputGroup from 'primevue/inputgroup';
import InputText from 'primevue/inputtext';
import InputGroupAddon from 'primevue/inputgroupaddon';
import { computed } from 'vue';
import { publishToast } from '@/utils/Toast.ts';
import { useToast } from 'primevue/usetoast';

const props = defineProps<{
    title: string;
}>();

const emit = defineEmits<{
    'external-share-started': [void];
}>();

const toast = useToast();

const urlOfPage = computed<string>(() => window.location.href);
const linkedInUrl = computed<string>(() => `https://www.linkedin.com/sharing/share-offsite/?url=${urlOfPage.value}`);

function copyLink (): void {
    navigator.clipboard.writeText(urlOfPage.value);
    publishToast(toast, 'Copied to clipboard', 'success');
}

function shareLinkedin (): void {
    navigator.clipboard.writeText(urlOfPage.value);
    publishToast(toast, 'Copied to clipboard. Please paste it on the Linkedin window.', 'success');
    window.open(linkedInUrl.value, 'linkedin-popup', 'width=600,height=600');
    emit('external-share-started');
}

function shareEmail (): void {
    navigator.clipboard.writeText(urlOfPage.value);
    const subject = encodeURIComponent(`LCP Delta: ${props.title}`);
    const body = encodeURIComponent(`Check this out on Spark: ${urlOfPage.value}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    emit('external-share-started');
}

function shareTeams (): void {
    try {
        navigator.clipboard.writeText(urlOfPage.value);
        const encodedUrl = encodeURIComponent(urlOfPage.value);
        const teamsUrl = `https://teams.microsoft.com/share?href=${encodedUrl}`;

        publishToast(toast, 'Copied to clipboard. Teams will open in a new window.', 'success');
        window.open(teamsUrl, 'teams-popup', 'width=600,height=600');
        emit('external-share-started');
    } catch {
        publishToast(toast, 'Unable to share to Teams', 'error');
    }
}
</script>

<style scoped lang="scss">
.share-url {
    display: flex;
    flex-direction: column;
    gap: var(--extraLargeSpacing);
    padding: var(--defaultSpacing);
    justify-content: space-between;
    border-radius: var(--borderRadiusOuter);

    .copy-link-input {
        width: 100%;
        display: flex;
        justify-content: center;

        .p-inputgroup {
            width: 60rem;
        }
    }

    .button-holder {
        display: flex;
        gap: var(--defaultSpacing);
        justify-content: center;
    }

    .link {
        cursor: pointer;
        color: var(--actionColour);
    }

    .teams-image {
        width: 2rem;
        height: 2rem;
        border-radius: var(--borderRadiusInner);
    }
}
</style>
